import { Save } from "@mui/icons-material";
import { Autocomplete, Box, TextField } from "@mui/material";
import React, { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createUserApi, updateUserApi } from "../../api/users/usersApi";
import LoadingButton from "../../elements/LoadingButton";
import { showSnackbar } from "../../redux/actions/commonAction";
import { getAllCredentials } from "../../redux/actions/credentialAction";
import { getAllUsers } from "../../redux/actions/usersAction";
import { debounce } from "../../utils/helpers";
import regex from "../../utils/regex";
import { useStyles } from "./styles";

const formFields = [
  { name: "firstName", label: "First name" },
  { name: "lastName", label: "Last name" },
  { name: "email", label: "Email" },
  { name: "roles", label: "Roles" },
  { name: "accounts", label: "Account number" },
  { name: "supervisorName", label: "Supervisor Name" },
  { name: "supervisorEmail", label: "Supervisor Email" },
];

const roles = [
  { key: "admin", value: "Admin" },
  { key: "user", value: "User" },
  // { key: "superUser", value: "Super User" },
  // { key: "provisionUser", value: "Provision User" },
];

const ManageCredentials = ({ edit, defaultValue = {}, onClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { allCredentials } = useSelector((state) => state.credential);
  const [state, setState] = useState(defaultValue);
  const [isValid, setIsValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({});

  useEffect(() => {
    dispatch(getAllCredentials());
  }, [dispatch]);

  useEffect(() => {
    if (edit) {
      const newState = {
        ...defaultValue,
        accounts: allCredentials.filter((obj) =>
          defaultValue.accounts.includes(obj.accountNumber)
        ),
        roles: roles.filter((obj) => defaultValue.roles.includes(obj.key)),
      };
      setState(newState);
    }
  }, [edit, defaultValue, allCredentials]);

  useEffect(() => {
    setIsValid(checkValidity());
    //eslint-disable-next-line
  }, [setIsValid, state]);

  const checkValidity = () => {
    const isValidState = formFields.every(
      (field) => state?.[field.name]?.length > 0
    );
    const isEmailValid =
      regex.validEmail.test(state?.email) &&
      regex.validEmail.test(state?.supervisorEmail);
    return isValidState && isEmailValid;
  };

  const debouncedValidation = debounce((key, value) => {
    let result = "";
    if (value.length > 0 && !regex.validEmail.test(value)) {
      result = "Invalid email!";
    }
    setError({ ...error, [key]: result });
  });

  const handleChange = (key, value) => {
    if (["email", "supervisorEmail"].includes(key)) {
      debouncedValidation(key, value);
    }
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let payload = {
      ...state,
      accounts: state.accounts.map((acc) => acc.accountNumber),
      roles: state.roles.map((acc) => acc.key),
    };
    const fullName = `${state.firstName} ${state.lastName}`;
    setLoading(true);
    const result = edit
      ? await updateUserApi(state?.email, payload)
      : await createUserApi(payload);
    setLoading(false);
    if (result) {
      onClose();
      dispatch(
        showSnackbar({
          message: edit ? `${fullName} Updated Successfully` : result,
          severity: "success",
          open: true,
        })
      );
      dispatch(getAllUsers());
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box>
        {formFields.map((data) => {
          if (["roles", "accounts"].includes(data.name)) {
            const options = data.name === "roles" ? roles : allCredentials;
            const label = data.name === "roles" ? "value" : "credName";
            return (
              <Autocomplete
                key={data.name}
                multiple
                fullWidth
                classes={{ option: classes.option }}
                options={options}
                value={state?.[data.name]}
                onChange={(e, value) => {
                  handleChange(data.name, value);
                }}
                getOptionLabel={(option) => option[label]}
                renderInput={(params) => (
                  <TextField {...data} {...params} margin="normal" />
                )}
              />
            );
          }
          return (
            <TextField
              {...data}
              key={data.name}
              required
              fullWidth
              error={Boolean(error[data.name])}
              helperText={error[data.name]}
              disabled={defaultValue && data.name === "email"}
              margin="normal"
              value={state?.[data.name]}
              onChange={(e) => handleChange(data.name, e.target.value)}
            />
          );
        })}
      </Box>
      <Box className={classes.footer}>
        <LoadingButton
          type="submit"
          disabled={!isValid}
          loading={loading}
          startIcon={<Save />}
          label={edit ? "Update" : "Save"}
        />
      </Box>
    </Box>
  );
};

export default memo(ManageCredentials);
